import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { dms } from "./api";
import { PageHeader } from "./OwnerPages";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, ChevronDown, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = { regional_manager: "Regional Manager", team_leader: "Team Leader", salesperson: "Salesperson" };
const todayIst = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
const errMsg = (e) => e?.response?.data?.detail || "Failed to load";
const hhmm = (s) => {
  if (!s) return "-";
  const str = String(s);
  const iso = /[zZ]|[+-]\d\d:?\d\d$/.test(str) ? str : str + "Z";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
};

function downloadCsv(name, header, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const body = [header, ...rows].map(r => r.map(esc).join(",")).join("\n");
  const blob = new Blob(["\ufeff" + body], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function Tile({ label, value, tone, onClick, testid }) {
  return (
    <Card
      className={`p-4 ${onClick ? "cursor-pointer hover:shadow-md hover:border-amber-400 transition" : ""}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter") onClick(); } : undefined}
      data-testid={testid}
    >
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${tone || "text-slate-900"}`}>{value}</div>
    </Card>
  );
}

// ---------- tree helpers ----------
function focusTree(data, focus) {
  if (!data || !focus) return data;
  const base = { ...data, unassigned_team_leaders: [], unassigned_salespersons: [] };
  const rm = (data.tree || []).find(r => r.id === focus);
  if (rm) return { ...base, tree: [rm] };
  for (const r of data.tree || []) {
    for (const t of r.team_leaders || []) {
      if (t.id === focus) return { ...base, tree: [{ ...r, team_leaders: [t] }] };
      const sp = (t.salespersons || []).filter(s => s.id === focus);
      if (sp.length) return { ...base, tree: [{ ...r, team_leaders: [{ ...t, salespersons: sp }] }] };
    }
  }
  const lt = (data.unassigned_team_leaders || []).find(t => t.id === focus);
  if (lt) return { ...base, tree: [], unassigned_team_leaders: [lt] };
  const ls = (data.unassigned_salespersons || []).filter(s => s.id === focus);
  if (ls.length) return { ...base, tree: [], unassigned_salespersons: ls };
  return data;
}

function allIds(data) {
  const ids = ["__ls"];
  (data?.tree || []).forEach(r => { ids.push(r.id); (r.team_leaders || []).forEach(t => ids.push(t.id)); });
  (data?.unassigned_team_leaders || []).forEach(t => ids.push(t.id));
  return ids;
}

function allPeople(data) {
  const m = new Map();
  const add = (n) => { if (n && !m.has(n.id)) m.set(n.id, n); };
  (data?.tree || []).forEach(r => { add(r); (r.team_leaders || []).forEach(t => { add(t); (t.salespersons || []).forEach(add); }); });
  (data?.unassigned_team_leaders || []).forEach(t => { add(t); (t.salespersons || []).forEach(add); });
  (data?.unassigned_salespersons || []).forEach(add);
  return [...m.values()];
}

// ---------- dropdown section ----------
function Section({ id, open, toggle, depth, head, children }) {
  const isOpen = open.has(id);
  return (
    <div className={`border rounded-lg ${depth ? "bg-slate-50/60" : "bg-white"}`} data-testid={`hr-sec-${id}`}>
      <button type="button" onClick={() => toggle(id)} className="w-full flex items-start gap-2 p-3 text-left">
        <span className="mt-0.5 text-slate-400">{isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
        <div className="flex-1 min-w-0">{head}</div>
      </button>
      {isOpen && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
}

function DayHead({ n, title, role }) {
  const t = n.team;
  const pct = t && t.total ? Math.round((t.punched * 100) / t.total) : 0;
  return (
    <div>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <div className="font-semibold text-slate-900">{title}</div>
          <div className="text-[11px] text-slate-500">{role} | {n.punched ? `in ${hhmm(n.in_at)}` : "no punch"}</div>
        </div>
        {t && (
          <div className="text-right text-xs text-slate-600">
            <b className="text-emerald-600">{t.punched}</b>/{t.total} punched in
            <div>avg {t.avg_hours} h | {t.km} km</div>
          </div>
        )}
      </div>
      {t && <div className="h-1.5 bg-slate-100 rounded mt-2 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} /></div>}
    </div>
  );
}

function MonthHead({ n, title, role }) {
  const t = n.team;
  return (
    <div className="flex items-start justify-between gap-2 flex-wrap">
      <div>
        <div className="font-semibold text-slate-900">{title}</div>
        <div className="text-[11px] text-slate-500">{role} | {n.days_present} days present</div>
      </div>
      {t && (
        <div className="text-right text-xs text-slate-600">
          <b>{t.present_days}</b> person-days | avg <b>{t.avg_present}</b> present/day
          <div>avg {t.avg_hours} h/day | {t.late_days} late | {t.km} km</div>
        </div>
      )}
    </div>
  );
}

function DayRows({ rows, onRoute }) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>In</TableHead><TableHead>Out</TableHead>
          <TableHead>Working</TableHead><TableHead>Km</TableHead><TableHead></TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-4 text-slate-400">No staff found</TableCell></TableRow>}
          {rows.map(n => (
            <TableRow key={n.id}>
              <TableCell className="font-medium">{n.name}</TableCell>
              <TableCell className="text-xs text-slate-500">{ROLE_LABEL[n.role] || n.role}</TableCell>
              <TableCell className="text-xs">{n.punched ? hhmm(n.in_at) : <span className="text-rose-600 font-semibold">No punch</span>}</TableCell>
              <TableCell className="text-xs">{!n.punched ? "-" : n.in_progress ? <span className="text-emerald-600">Working</span> : hhmm(n.out_at)}</TableCell>
              <TableCell className="text-xs font-semibold">{n.punched ? `${n.working_hours} h` : "-"}</TableCell>
              <TableCell className="text-xs font-semibold">{n.punched ? `${n.distance_km} km` : "-"}</TableCell>
              <TableCell><Button size="sm" variant="outline" onClick={() => onRoute(n)} data-testid={`hr-route-${n.id}`}>Route</Button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function MonthRows({ rows, onRoute }) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Days present</TableHead><TableHead>Total hours</TableHead>
          <TableHead>Avg hours</TableHead><TableHead>Avg first in</TableHead><TableHead>Late days</TableHead><TableHead>Km</TableHead><TableHead></TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-4 text-slate-400">No staff found</TableCell></TableRow>}
          {rows.map(n => (
            <TableRow key={n.id}>
              <TableCell className="font-medium">{n.name}</TableCell>
              <TableCell className="text-xs text-slate-500">{ROLE_LABEL[n.role] || n.role}</TableCell>
              <TableCell className="text-xs font-semibold">{n.days_present}</TableCell>
              <TableCell className="text-xs font-semibold">{n.days_present ? `${n.total_hours} h` : "-"}</TableCell>
              <TableCell className="text-xs">{n.days_present ? `${n.avg_hours} h` : "-"}</TableCell>
              <TableCell className="text-xs">{n.avg_first_in || "-"}</TableCell>
              <TableCell className={`text-xs font-semibold ${n.late_days ? "text-rose-600" : ""}`}>{n.late_days}</TableCell>
              <TableCell className="text-xs font-semibold">
                {n.km} km{n.no_gps && <span className="ml-1 text-[10px] text-amber-700 bg-amber-100 rounded px-1">No GPS</span>}
              </TableCell>
              <TableCell><Button size="sm" variant="outline" onClick={() => onRoute(n)} data-testid={`hr-route-${n.id}`}>Route</Button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function DailyTable({ daily }) {
  return (
    <Table>
      <TableHeader><TableRow>
        <TableHead>Date</TableHead><TableHead>Punched in</TableHead><TableHead>Avg hours</TableHead><TableHead>Km</TableHead>
      </TableRow></TableHeader>
      <TableBody>
        {(daily || []).map(d => (
          <TableRow key={d.date}>
            <TableCell className="text-xs">{d.date}</TableCell>
            <TableCell className="text-xs font-semibold">{d.punched}/{d.total}</TableCell>
            <TableCell className="text-xs">{d.punched ? `${d.avg_hours} h` : "-"}</TableCell>
            <TableCell className="text-xs">{d.km} km</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function DailyBlock({ team }) {
  if (!team?.daily?.length) return null;
  return (
    <details className="mt-3">
      <summary className="cursor-pointer text-xs font-semibold text-amber-700">Day-wise report of this team</summary>
      <div className="max-h-64 overflow-y-auto mt-2"><DailyTable daily={team.daily} /></div>
    </details>
  );
}

function Teams({ data, open, toggle, Head, Rows, onRoute, withDaily }) {
  const tlSec = (t) => (
    <Section key={t.id} id={t.id} open={open} toggle={toggle} depth={1} head={<Head n={t} title={`Team of ${t.name}`} role="Team Leader" />}>
      <Rows rows={[t, ...(t.salespersons || [])]} onRoute={onRoute} />
      {withDaily && <DailyBlock team={t.team} />}
    </Section>
  );
  const out = (data?.tree || []).map(rm => (
    <Section key={rm.id} id={rm.id} open={open} toggle={toggle} depth={0} head={<Head n={rm} title={`Team of ${rm.name}`} role="Regional Manager" />}>
      <Rows rows={[rm]} onRoute={onRoute} />
      <div className="mt-3 space-y-2">
        {(rm.team_leaders || []).length === 0 && <div className="text-xs text-slate-400">No Team Leaders assigned</div>}
        {(rm.team_leaders || []).map(tlSec)}
      </div>
      {withDaily && <DailyBlock team={rm.team} />}
    </Section>
  ));
  const lt = data?.unassigned_team_leaders || [];
  if (lt.length) out.push(
    <div key="lt" className="space-y-2">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Team Leaders not under any Regional Manager</div>
      {lt.map(tlSec)}
    </div>
  );
  const ls = data?.unassigned_salespersons || [];
  if (ls.length) out.push(
    <Section key="ls" id="__ls" open={open} toggle={toggle} depth={0} head={<div className="font-semibold">Salespersons not under any Team Leader ({ls.length})</div>}>
      <Rows rows={ls} onRoute={onRoute} />
    </Section>
  );
  if (!out.length) return <Card className="p-6 text-center text-slate-400">No teams found</Card>;
  return <div className="space-y-3">{out}</div>;
}

// ---------- GPS route dialog ----------
function TrailDialog({ person, date: startDate, onClose }) {
  const [date, setDate] = useState(startDate);
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    dms.hrLocationTrail(person.id, date)
      .then(r => { if (alive) setT(r); })
      .catch(e => { if (alive) { setT(null); toast.error(errMsg(e)); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [person.id, date]);
  const pts = t?.points || [];
  const sketch = useMemo(() => {
    if (!pts.length) return null;
    const W = 640, H = 260, PAD = 16;
    const lats = pts.map(p => p.lat), lngs = pts.map(p => p.lng);
    const minLat = Math.min(...lats), maxLat = Math.max(...lats), minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
    const kx = Math.cos(((minLat + maxLat) / 2) * Math.PI / 180);
    const spanX = Math.max((maxLng - minLng) * kx, 1e-6), spanY = Math.max(maxLat - minLat, 1e-6);
    const k = Math.min((W - 2 * PAD) / spanX, (H - 2 * PAD) / spanY);
    const X = (lng) => PAD + (lng - minLng) * kx * k;
    const Y = (lat) => H - PAD - (lat - minLat) * k;
    return { W, H, X, Y, line: pts.map(p => `${X(p.lng).toFixed(1)},${Y(p.lat).toFixed(1)}`).join(" ") };
  }, [t]); // eslint-disable-line
  const routeUrl = (() => {
    if (pts.length < 2) return null;
    const f = (p) => `${p.lat},${p.lng}`;
    const mid = pts.slice(1, -1);
    const step = Math.max(1, Math.ceil(mid.length / 8));
    const wp = mid.filter((_, i) => i % step === 0).slice(0, 8).map(f);
    const w = wp.length ? `&waypoints=${encodeURIComponent(wp.join("|"))}` : "";
    return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(f(pts[0]))}&destination=${encodeURIComponent(f(pts[pts.length - 1]))}${w}&travelmode=driving`;
  })();
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="hr-trail-dialog">
      <div className="bg-white dark:bg-slate-900 rounded-xl w-full max-w-3xl mt-6 p-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-lg font-bold">{person.name} - route</div>
            <div className="text-xs text-slate-500">{ROLE_LABEL[person.role] || person.role}</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500"><X size={20} /></button>
        </div>
        <div className="flex items-center gap-3 my-3 flex-wrap">
          <Input type="date" value={date} onChange={e => e.target.value && setDate(e.target.value)} className="w-44" />
          {t && <>
            <span className="text-xs bg-slate-100 rounded-full px-2 py-1">GPS points: <b>{t.gps_points}</b></span>
            <span className="text-xs bg-slate-100 rounded-full px-2 py-1">Distance: <b>{t.km} km</b></span>
            {t.first_at && <span className="text-xs bg-slate-100 rounded-full px-2 py-1">First {t.first_at} | Last {t.last_at}</span>}
            <span className="text-xs bg-slate-100 rounded-full px-2 py-1">Stops: <b>{(t.stops || []).length}</b></span>
          </>}
        </div>
        {loading && <div className="py-8 text-center text-slate-400">Loading...</div>}
        {!loading && t && t.gps_points === 0 && (
          <div className="p-4 rounded-lg bg-amber-50 border border-amber-300 text-sm text-amber-900">
            No GPS data saved for this person on this date. The phone is not sending locations (or tracking was off), so km shows 0.
          </div>
        )}
        {!loading && t && t.gps_points > 0 && (
          <div className="space-y-4">
            {sketch && (
              <div>
                <svg viewBox={`0 0 ${sketch.W} ${sketch.H}`} className="w-full border rounded-lg bg-slate-50">
                  <polyline points={sketch.line} fill="none" stroke="#2563eb" strokeWidth="2" strokeLinejoin="round" />
                  {(t.stops || []).map((s, i) => <circle key={i} cx={sketch.X(s.lng)} cy={sketch.Y(s.lat)} r="6" fill="#f59e0b" stroke="#fff" strokeWidth="1.5" />)}
                  <circle cx={sketch.X(pts[0].lng)} cy={sketch.Y(pts[0].lat)} r="6" fill="#16a34a" stroke="#fff" strokeWidth="1.5" />
                  <circle cx={sketch.X(pts[pts.length - 1].lng)} cy={sketch.Y(pts[pts.length - 1].lat)} r="6" fill="#dc2626" stroke="#fff" strokeWidth="1.5" />
                </svg>
                <div className="text-[11px] text-slate-500 mt-1">Green = start, red = last point, orange = stops. This is a path sketch, not a map.</div>
              </div>
            )}
            {routeUrl && <a href={routeUrl} target="_blank" rel="noreferrer" className="inline-block text-sm font-semibold text-blue-600 underline">Open full route in Google Maps</a>}
            <div>
              <div className="font-semibold mb-1">Stops (stayed 10+ minutes)</div>
              <Table>
                <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Stayed</TableHead><TableHead>Location</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(t.stops || []).length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-3 text-slate-400">No long stops</TableCell></TableRow>}
                  {(t.stops || []).map((s, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs">{s.from} - {s.to}</TableCell>
                      <TableCell className="text-xs font-semibold">{s.minutes} min</TableCell>
                      <TableCell className="text-xs">
                        {s.lat.toFixed(5)}, {s.lng.toFixed(5)}{" "}
                        <a href={`https://www.google.com/maps?q=${s.lat},${s.lng}`} target="_blank" rel="noreferrer" className="text-blue-600 underline">Map</a>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <details>
              <summary className="cursor-pointer text-sm font-semibold">All location points ({pts.length})</summary>
              <div className="max-h-60 overflow-y-auto mt-2">
                <Table>
                  <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Lat, Lng</TableHead><TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {pts.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell className="text-xs">{p.t}</TableCell>
                        <TableCell className="text-xs">{p.lat.toFixed(5)}, {p.lng.toFixed(5)}</TableCell>
                        <TableCell className="text-xs"><a href={`https://www.google.com/maps?q=${p.lat},${p.lng}`} target="_blank" rel="noreferrer" className="text-blue-600 underline">Map</a></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}

const useToggle = () => {
  const [open, setOpen] = useState(new Set());
  const toggle = (id) => setOpen(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return [open, setOpen, toggle];
};

// ---------- Dashboard ----------
export function HrDashboardPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [date, setDate] = useState(todayIst());
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, , toggle] = useToggle();
  const [route, setRoute] = useState(null);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    dms.hrAttendanceTree(date)
      .then(r => { if (alive) setD(r); })
      .catch(e => { if (alive) { setD(null); toast.error(errMsg(e)); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [date]);
  const go = (extra) => nav(`/dms/hr/attendance?${new URLSearchParams({ date, ...(extra || {}) }).toString()}`);
  const s = d?.summary;
  return (
    <div data-testid="hr-dashboard">
      <PageHeader title="HR Dashboard" subtitle={`Welcome, ${user?.name || ""} - team-wise attendance${d?.scoped ? " (your assigned Regional Managers)" : ""}`} />
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <Input type="date" value={date} onChange={e => e.target.value && setDate(e.target.value)} className="w-44" data-testid="hr-dash-date" />
        <Button variant="outline" onClick={() => setDate(todayIst())}>Today</Button>
        <Button variant="outline" onClick={() => nav("/dms/hr/attendance?mode=month")}>Month report</Button>
        <span className="text-xs text-slate-500">Click a tile for the list, click a team to open it</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Tile label="Field staff" value={s ? s.total : "-"} onClick={() => go()} testid="hr-tile-all" />
        <Tile label="Punched in" value={s ? s.punched : "-"} tone="text-emerald-600" onClick={() => go({ status: "in" })} testid="hr-tile-in" />
        <Tile label="No punch yet" value={s ? s.no_punch : "-"} tone="text-rose-600" onClick={() => go({ status: "none" })} testid="hr-tile-none" />
        <Tile label="Avg working hours" value={s ? `${s.avg_hours} h` : "-"} onClick={() => go({ mode: "month" })} testid="hr-tile-avg" />
      </div>
      {loading ? <Card className="p-6 text-center text-slate-400">Loading teams...</Card>
        : <Teams data={d} open={open} toggle={toggle} Head={DayHead} Rows={DayRows} onRoute={(n) => setRoute({ person: n, date })} />}
      {route && <TrailDialog person={route.person} date={route.date} onClose={() => setRoute(null)} />}
    </div>
  );
}

// ---------- Attendance (Day / Month) ----------
export function HrAttendancePage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const focus = sp.get("focus") || "";
  const status = sp.get("status") || "";
  const [mode, setMode] = useState(sp.get("mode") === "month" ? "month" : "day");
  const [date, setDate] = useState(sp.get("date") || todayIst());
  const [month, setMonth] = useState(todayIst().slice(0, 7));
  const [lateAfter, setLateAfter] = useState("10:30");
  const [loaded, setLoaded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen, toggle] = useToggle();
  const [route, setRoute] = useState(null);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    const req = mode === "day" ? dms.hrAttendanceTree(date) : dms.hrAttendanceReport(month, lateAfter || "10:30");
    req.then(r => { if (alive) setLoaded({ mode, d: r }); })
      .catch(e => { if (alive) { setLoaded(null); toast.error(errMsg(e)); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [mode, date, month, lateAfter]);
  const data = loaded && loaded.mode === mode ? loaded.d : null;
  const view = useMemo(() => focusTree(data, focus), [data, focus]);
  useEffect(() => { if (focus && view) setOpen(new Set(allIds(view))); }, [focus, view]); // eslint-disable-line
  const flat = !!q.trim() || (mode === "day" && !!status);
  const flatRows = useMemo(() => {
    if (!flat) return [];
    const term = q.trim().toLowerCase();
    return allPeople(view).filter(n => (!term || (n.name || "").toLowerCase().includes(term))
      && (mode !== "day" || !status || (status === "in" ? n.punched : !n.punched)));
  }, [flat, view, q, status, mode]);
  const Head = mode === "day" ? DayHead : MonthHead;
  const Rows = mode === "day" ? DayRows : MonthRows;
  const s = data?.summary;
  const onRoute = (n) => setRoute({ person: n, date: mode === "day" ? date : (month === todayIst().slice(0, 7) ? todayIst() : `${month}-01`) });
  const doCsv = () => {
    const people = allPeople(view);
    if (mode === "day") {
      downloadCsv(`attendance_${date}.csv`, ["Name", "Role", "In", "Out", "Working hours", "Km"],
        people.map(n => [n.name, ROLE_LABEL[n.role] || n.role, n.punched ? hhmm(n.in_at) : "No punch", !n.punched ? "" : n.in_progress ? "Working" : hhmm(n.out_at), n.punched ? n.working_hours : "", n.punched ? n.distance_km : ""]));
    } else {
      downloadCsv(`attendance_${month}.csv`, ["Name", "Role", "Days present", "Total hours", "Avg hours", "Avg first in", "Late days", "Km"],
        people.map(n => [n.name, ROLE_LABEL[n.role] || n.role, n.days_present, n.total_hours, n.avg_hours, n.avg_first_in || "", n.late_days, n.km]));
    }
  };
  const base = `/dms/hr/attendance`;
  return (
    <div data-testid="hr-attendance-page">
      <PageHeader title="Attendance" subtitle="Teams by hierarchy. Click a team to open it (Regional Manager > Team Leader > Salesperson)" />
      <Card className="p-3 mb-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant={mode === "day" ? "default" : "outline"} onClick={() => setMode("day")} data-testid="hr-mode-day">Day</Button>
          <Button variant={mode === "month" ? "default" : "outline"} onClick={() => setMode("month")} data-testid="hr-mode-month">Month</Button>
          {mode === "day" ? (
            <>
              <Input type="date" value={date} onChange={e => e.target.value && setDate(e.target.value)} className="w-44" data-testid="hr-att-date" />
              <Button variant="outline" onClick={() => setDate(todayIst())}>Today</Button>
            </>
          ) : (
            <>
              <Input type="month" value={month} onChange={e => e.target.value && setMonth(e.target.value)} className="w-44" data-testid="hr-att-month" />
              <div className="flex items-center gap-2 text-sm text-slate-600">Late after
                <Input type="time" value={lateAfter} onChange={e => e.target.value && setLateAfter(e.target.value)} className="w-32" />
              </div>
            </>
          )}
          <div className="relative max-w-xs flex-1 min-w-[180px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name..." className="pl-9" data-testid="hr-att-search" />
          </div>
          {!flat && <>
            <Button variant="outline" onClick={() => setOpen(new Set(allIds(view)))}>Open all</Button>
            <Button variant="outline" onClick={() => setOpen(new Set())}>Close all</Button>
          </>}
          <Button variant="outline" onClick={doCsv} disabled={!data}>Download CSV</Button>
        </div>
      </Card>
      {(focus || (mode === "day" && status)) && (
        <Card className="p-3 mb-3 flex items-center gap-3 flex-wrap bg-amber-50 border-amber-300">
          <span className="text-sm text-slate-700">
            Showing: {focus ? "selected team / person" : ""}{focus && status ? " + " : ""}{status === "in" ? "punched in only" : status === "none" ? "no punch only" : ""}
          </span>
          <Button variant="outline" size="sm" onClick={() => nav(base)} data-testid="hr-clear-filter">Clear filter</Button>
        </Card>
      )}
      {mode === "day" ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
          <Tile label="Staff" value={s ? s.total : "-"} onClick={() => nav(`${base}?date=${date}`)} />
          <Tile label="Punched in" value={s ? s.punched : "-"} tone="text-emerald-600" onClick={() => nav(`${base}?date=${date}&status=in`)} />
          <Tile label="No punch" value={s ? s.no_punch : "-"} tone="text-rose-600" onClick={() => nav(`${base}?date=${date}&status=none`)} />
          <Tile label="Avg hours" value={s ? `${s.avg_hours} h` : "-"} />
          <Tile label="Total km" value={s ? `${s.km} km` : "-"} />
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-3">
          <Tile label="Staff" value={s ? s.members : "-"} />
          <Tile label="Present person-days" value={s ? s.present_days : "-"} tone="text-emerald-600" />
          <Tile label="Avg present / day" value={s ? s.avg_present : "-"} />
          <Tile label="Avg hours / day" value={s ? `${s.avg_hours} h` : "-"} />
          <Tile label="Late days" value={s ? s.late_days : "-"} tone="text-rose-600" />
          <Tile label="Total km" value={s ? `${s.km} km` : "-"} />
        </div>
      )}
      {mode === "month" && data && (
        <>
          <div className="text-xs text-slate-500 mb-2">
            {data.from} to {data.to} | late = first punch-in after {data.late_after} IST | person-day = one person with a punch on one day. Leave and holiday data is not in the system yet, so a day without punch is not marked absent.
          </div>
          <Card className="p-3 mb-3">
            <div className="font-semibold mb-2">Day-wise report (all teams)</div>
            <div className="max-h-72 overflow-y-auto"><DailyTable daily={s?.daily} /></div>
          </Card>
        </>
      )}
      {loading && <Card className="p-6 text-center text-slate-400">Loading...</Card>}
      {!loading && data && flat && <Card className="p-2"><Rows rows={flatRows} onRoute={onRoute} /></Card>}
      {!loading && data && !flat && <Teams data={view} open={open} toggle={toggle} Head={Head} Rows={Rows} onRoute={onRoute} withDaily={mode === "month"} />}
      {route && <TrailDialog person={route.person} date={route.date} onClose={() => setRoute(null)} />}
    </div>
  );
}

// ---------- Performance + Salary ----------
const PayCtx = React.createContext({ save: () => {}, details: () => {} });
const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

function SalaryInput({ value, onCommit, w }) {
  const [v, setV] = useState(String(value ?? 0));
  useEffect(() => { setV(String(value ?? 0)); }, [value]);
  return (
    <Input
      type="number" min="0" value={v} readOnly disabled title="Edit in Salary Master" className={`h-8 ${w}`}
      onChange={e => setV(e.target.value)}
      onBlur={() => { if (Number(v || 0) !== Number(value || 0)) onCommit(Number(v || 0)); }}
      onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
    />
  );
}

function PayHead({ n, title, role }) {
  const t = n.team;
  return (
    <div className="flex items-start justify-between gap-2 flex-wrap">
      <div>
        <div className="font-semibold text-slate-900">{title}</div>
        <div className="text-[11px] text-slate-500">{role} | own payable {n.payable_days} days</div>
      </div>
      {t && (
        <div className="text-right text-xs text-slate-600">
          <b>{t.members}</b> staff | payable <b className="text-emerald-700">{inr(t.net_total)}</b> of {inr(t.monthly_total)}
          <div>{t.absent_days} absent days | {t.late_days} late{t.no_salary ? ` | ${t.no_salary} salary not set` : ""}</div>
        </div>
      )}
    </div>
  );
}

function PayRows({ rows }) {
  const ctx = React.useContext(PayCtx);
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Monthly salary</TableHead><TableHead>Rs/km</TableHead>
          <TableHead>Present</TableHead><TableHead>Absent</TableHead><TableHead>Week off</TableHead><TableHead>Late</TableHead>
          <TableHead>Payable days</TableHead><TableHead>Earned</TableHead><TableHead>Km allow.</TableHead><TableHead>Net</TableHead><TableHead></TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={13} className="text-center py-4 text-slate-400">No staff found</TableCell></TableRow>}
          {rows.map(n => (
            <TableRow key={n.id}>
              <TableCell className="font-medium">{n.name}</TableCell>
              <TableCell className="text-xs text-slate-500">{ROLE_LABEL[n.role] || n.role}</TableCell>
              <TableCell><SalaryInput value={n.monthly_salary} w="w-28" onCommit={(v) => ctx.save(n.id, { monthly_salary: v })} /></TableCell>
              <TableCell><SalaryInput value={n.per_km_rate} w="w-20" onCommit={(v) => ctx.save(n.id, { per_km_rate: v })} /></TableCell>
              <TableCell className="text-xs font-semibold">{n.full_days} full{n.half_days ? ` + ${n.half_days} half` : ""}</TableCell>
              <TableCell className={`text-xs font-semibold ${n.absent_days ? "text-rose-600" : ""}`}>{n.absent_days}</TableCell>
              <TableCell className="text-xs">{n.weekly_offs}</TableCell>
              <TableCell className={`text-xs font-semibold ${n.late_days ? "text-rose-600" : ""}`}>{n.late_days}{n.late_deduct_days ? ` (-${n.late_deduct_days}d)` : ""}</TableCell>
              <TableCell className="text-xs font-semibold">{n.payable_days}</TableCell>
              <TableCell className="text-xs font-semibold">{n.monthly_salary > 0 ? inr(n.earned) : <span className="text-amber-700">Set salary</span>}</TableCell>
              <TableCell className="text-xs">{n.km_allowance ? inr(n.km_allowance) : "-"}</TableCell>
              <TableCell className="text-xs font-bold text-emerald-700">{n.monthly_salary > 0 ? inr(n.net) : "-"}</TableCell>
              <TableCell><Button size="sm" variant="outline" onClick={() => ctx.details(n)} data-testid={`hr-pay-details-${n.id}`}>Details</Button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function PayDetails({ n, data, onClose }) {
  const color = { P: "text-emerald-700", H: "text-amber-700", A: "text-rose-600", W: "text-slate-500", T: "text-blue-600" };
  const label = { P: "Full day", H: "Half day", A: "Absent", W: "Weekly off", T: "Today (pending)" };
  const r = data.rules;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl w-full max-w-2xl mt-6 p-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-lg font-bold">{n.name} - salary calculation</div>
            <div className="text-xs text-slate-500">{ROLE_LABEL[n.role] || n.role} | {data.month}</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500"><X size={20} /></button>
        </div>
        <div className="text-sm space-y-1 my-3">
          <div>Monthly salary <b>{inr(n.monthly_salary)}</b> / {data.divisor} days = <b>{inr(n.per_day)}</b> per day</div>
          <div>Worked days: {n.full_days} full + {n.half_days} half x 0.5 = <b>{n.full_days + n.half_days * 0.5}</b></div>
          <div>Paid weekly offs: <b>{r.basis === "calendar" ? n.weekly_offs : 0}</b></div>
          <div>Late deduction: <b>{n.late_deduct_days}</b> days ({n.late_days} late marks)</div>
          <div>Payable days: <b>{n.payable_days}</b></div>
          <div>Earned = {inr(n.per_day)} x {n.payable_days} = <b>{inr(n.earned)}</b></div>
          <div>Km allowance: {n.km} km x Rs {n.per_km_rate} = <b>{inr(n.km_allowance)}</b></div>
          <div className="text-base">Net payable: <b className="text-emerald-700">{inr(n.net)}</b></div>
        </div>
        <div className="max-h-72 overflow-y-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead>First in</TableHead><TableHead>Hours</TableHead></TableRow></TableHeader>
            <TableBody>
              {(n.days || []).map(d => (
                <TableRow key={d.date}>
                  <TableCell className="text-xs">{d.date}</TableCell>
                  <TableCell className={`text-xs font-semibold ${color[d.status]}`}>{label[d.status]}</TableCell>
                  <TableCell className="text-xs">{d.in || "-"}</TableCell>
                  <TableCell className="text-xs">{d.in ? `${d.hours} h` : "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

function RulesPanel({ draft, setDraft, onSave }) {
  if (!draft) return null;
  const set = (k, v) => setDraft({ ...draft, [k]: v });
  const sel = "border rounded-md h-9 px-2 text-sm bg-transparent";
  return (
    <Card className="p-4 mb-3" data-testid="hr-rules-panel">
      <div className="font-semibold mb-3">Salary rules</div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
        <label className="flex flex-col gap-1">Weekly off
          <select className={sel} value={draft.weekly_off} onChange={e => set("weekly_off", e.target.value)}>
            <option value="sunday">Sunday (paid)</option><option value="none">None</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">Per day salary basis
          <select className={sel} value={draft.basis} onChange={e => set("basis", e.target.value)}>
            <option value="calendar">Days in month (Sundays paid)</option><option value="working">Working days only</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">Full day (hours or more)
          <Input type="number" step="0.5" value={draft.full_hours} onChange={e => set("full_hours", e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">Half day (hours or more)
          <Input type="number" step="0.5" value={draft.half_hours} onChange={e => set("half_hours", e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">Late after (first punch-in)
          <Input type="time" value={draft.late_after} onChange={e => e.target.value && set("late_after", e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">Late marks that cut half day (0 = off)
          <Input type="number" min="0" value={draft.late_per_deduct} onChange={e => set("late_per_deduct", e.target.value)} />
        </label>
      </div>
      <div className="mt-3"><Button onClick={onSave} data-testid="hr-rules-save">Save rules</Button></div>
    </Card>
  );
}

// ---------- Payroll run bar (finalize / reopen) ----------
function RunBar({ month, data, onChanged }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const fin = data?.finalized;
  const isOwner = ["owner", "super_admin"].includes(user?.role);
  const doFinalize = async () => {
    const unset = data?.summary?.no_salary || 0;
    const msg = unset
      ? `${unset} staff have no salary set and will count as 0. Finalize ${month}? Numbers will be frozen.`
      : `Finalize ${month}? Numbers will be frozen.`;
    if (!window.confirm(msg)) return;
    setBusy(true);
    try { await dms.hrPayrollFinalize({ month, allow_unset: true }); toast.success("Month finalized"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const doReopen = async () => {
    const reason = window.prompt("Reason to reopen this month?");
    if (!reason) return;
    setBusy(true);
    try { await dms.hrPayrollReopen({ month, reason }); toast.success("Month reopened"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  if (fin) {
    return (
      <Card className="p-3 mb-3 bg-emerald-50 border-emerald-300 flex items-center gap-3 flex-wrap" data-testid="hr-run-final">
        <span className="text-sm text-emerald-900">Month finalized by <b>{String(fin.by || "")}</b>. These numbers are frozen and will not change.</span>
        {isOwner && <Button size="sm" variant="outline" disabled={busy} onClick={doReopen}>Reopen (Owner)</Button>}
      </Card>
    );
  }
  return (
    <Card className="p-3 mb-3 flex items-center gap-3 flex-wrap" data-testid="hr-run-open">
      <span className="text-sm text-slate-600">
        {data?.partial ? "Month is in progress. You can finalize after it ends." : "Month is complete. Check the numbers, then finalize to freeze them."}
      </span>
      <Button size="sm" disabled={busy || !!data?.partial} onClick={doFinalize} data-testid="hr-finalize">Finalize month</Button>
    </Card>
  );
}

// ---------- Salary Master ----------
const MasterCtx = React.createContext({ edits: {}, setEdit: () => {}, history: () => {} });

function parseCsv(text) {
  const rows = [];
  let row = [], cur = "", q = false;
  const s = String(text || "").replace(/^\ufeff/, "");
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (q) {
      if (c === '"') { if (s[i + 1] === '"') { cur += '"'; i++; } else q = false; } else cur += c;
    } else if (c === '"') q = true;
    else if (c === ",") { row.push(cur); cur = ""; }
    else if (c === "\n" || c === "\r") { if (c === "\r" && s[i + 1] === "\n") i++; row.push(cur); rows.push(row); row = []; cur = ""; }
    else cur += c;
  }
  if (cur !== "" || row.length) { row.push(cur); rows.push(row); }
  return rows.filter(r => r.some(x => String(x).trim() !== ""));
}

function MasterHead({ n, title, role }) {
  const t = n.team;
  return (
    <div className="flex items-start justify-between gap-2 flex-wrap">
      <div>
        <div className="font-semibold text-slate-900">{title}</div>
        <div className="text-[11px] text-slate-500">{role}</div>
      </div>
      {t && (
        <div className="text-right text-xs text-slate-600">
          <b>{t.members}</b> staff | salary set <b className="text-emerald-700">{t.set}</b>
          {t.not_set ? <> | not set <b className="text-rose-600">{t.not_set}</b></> : null}
          <div>total {inr(t.monthly_total)} / month</div>
        </div>
      )}
    </div>
  );
}

function MasterRows({ rows }) {
  const ctx = React.useContext(MasterCtx);
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Name</TableHead><TableHead>Role</TableHead><TableHead>Current salary</TableHead>
          <TableHead>New monthly salary</TableHead><TableHead>New Rs/km</TableHead><TableHead></TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-4 text-slate-400">No staff found</TableCell></TableRow>}
          {rows.map(n => {
            const e = ctx.edits[n.id] || {};
            const dirty = e.monthly_salary !== undefined || e.per_km_rate !== undefined;
            return (
              <TableRow key={n.id} className={!n.salary_set ? "bg-amber-50/60" : ""}>
                <TableCell className="font-medium">{n.name}</TableCell>
                <TableCell className="text-xs text-slate-500">{ROLE_LABEL[n.role] || n.role}</TableCell>
                <TableCell className="text-xs">
                  {n.salary_set ? <><b>{inr(n.monthly_salary)}</b> <span className="text-slate-500">from {n.effective_from}</span></> : <span className="text-rose-600 font-semibold">Not set</span>}
                </TableCell>
                <TableCell>
                  <Input type="number" min="0" className={`h-8 w-32 ${dirty ? "border-amber-500" : ""}`}
                    value={e.monthly_salary !== undefined ? e.monthly_salary : (n.monthly_salary || "")}
                    onChange={ev => ctx.setEdit(n.id, { monthly_salary: ev.target.value })} data-testid={`hr-sal-${n.id}`} />
                </TableCell>
                <TableCell>
                  <Input type="number" min="0" className={`h-8 w-24 ${dirty ? "border-amber-500" : ""}`}
                    value={e.per_km_rate !== undefined ? e.per_km_rate : (n.per_km_rate || "")}
                    onChange={ev => ctx.setEdit(n.id, { per_km_rate: ev.target.value })} />
                </TableCell>
                <TableCell><Button size="sm" variant="outline" onClick={() => ctx.history(n)}>History</Button></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function SalaryHistory({ person, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let alive = true;
    dms.hrSalaryHistory(person.id).then(r => { if (alive) setD(r); }).catch(e => toast.error(errMsg(e)));
    return () => { alive = false; };
  }, [person.id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl w-full max-w-2xl mt-8 p-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="text-lg font-bold">{person.name} - salary history</div>
          <button type="button" onClick={onClose} className="text-slate-500"><X size={20} /></button>
        </div>
        {!d && <div className="py-6 text-center text-slate-400">Loading...</div>}
        {d && (
          <Table>
            <TableHeader><TableRow><TableHead>From month</TableHead><TableHead>Monthly salary</TableHead><TableHead>Rs/km</TableHead><TableHead>Changed by</TableHead><TableHead>When</TableHead></TableRow></TableHeader>
            <TableBody>
              {(d.data || []).length === 0 && !d.legacy && <TableRow><TableCell colSpan={5} className="text-center py-4 text-slate-400">No salary entered yet</TableCell></TableRow>}
              {(d.data || []).map(r => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs font-semibold">{r.effective_from}</TableCell>
                  <TableCell className="text-xs">{inr(r.monthly_salary)}</TableCell>
                  <TableCell className="text-xs">{r.per_km_rate || "-"}</TableCell>
                  <TableCell className="text-xs">{r.created_by_name || r.created_by}</TableCell>
                  <TableCell className="text-xs">{String(r.at || "").slice(0, 16).replace("T", " ")}</TableCell>
                </TableRow>
              ))}
              {d.legacy && (
                <TableRow>
                  <TableCell className="text-xs font-semibold">Earlier (before revisions)</TableCell>
                  <TableCell className="text-xs">{inr(d.legacy.monthly_salary)}</TableCell>
                  <TableCell className="text-xs">{d.legacy.per_km_rate || "-"}</TableCell>
                  <TableCell className="text-xs">{d.legacy.updated_by || "-"}</TableCell>
                  <TableCell className="text-xs">{String(d.legacy.at || "").slice(0, 16).replace("T", " ")}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

export function HrSalaryMasterPage() {
  const [month, setMonth] = useState(todayIst().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [q, setQ] = useState("");
  const [open, setOpen, toggle] = useToggle();
  const [edits, setEdits] = useState({});
  const [hist, setHist] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    dms.hrSalaryMaster(month)
      .then(r => { if (alive) setData(r); })
      .catch(e => { if (alive) { setData(null); toast.error(errMsg(e)); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [month, tick]);
  const people = useMemo(() => allPeople(data), [data]);
  const setEdit = (uid, patch) => setEdits(prev => ({ ...prev, [uid]: { ...(prev[uid] || {}), ...patch } }));
  const changed = useMemo(() => people.map(p => {
    const e = edits[p.id];
    if (!e) return null;
    const ms = e.monthly_salary !== undefined && e.monthly_salary !== "" ? Number(e.monthly_salary) : p.monthly_salary;
    const pk = e.per_km_rate !== undefined && e.per_km_rate !== "" ? Number(e.per_km_rate) : p.per_km_rate;
    if (isNaN(ms) || isNaN(pk)) return null;
    if (ms === p.monthly_salary && pk === p.per_km_rate) return null;
    return { user_id: p.id, monthly_salary: ms, per_km_rate: pk };
  }).filter(Boolean), [people, edits]);
  const flatRows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return term ? people.filter(n => (n.name || "").toLowerCase().includes(term)) : [];
  }, [people, q]);
  const saveAll = async () => {
    if (!changed.length) { toast.info("No changes to save"); return; }
    if (!window.confirm(`Save ${changed.length} salary change(s) effective from ${month}?`)) return;
    setSaving(true);
    try {
      const r = await dms.hrSalaryBulk({ effective_from: month, rows: changed });
      toast.success(`Saved ${r.saved}, unchanged ${r.skipped}${r.errors?.length ? `, errors ${r.errors.length}` : ""}`);
      setEdits({});
      setTick(t => t + 1);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };
  const template = () => downloadCsv(`salary_template_${month}.csv`,
    ["user_id", "name", "role", "monthly_salary", "rs_per_km"],
    people.map(n => [n.id, n.name, ROLE_LABEL[n.role] || n.role, n.monthly_salary || "", n.per_km_rate || ""]));
  const onFile = async (ev) => {
    const f = ev.target.files && ev.target.files[0];
    ev.target.value = "";
    if (!f) return;
    const rows = parseCsv(await f.text());
    if (rows.length < 2) { toast.error("File is empty"); return; }
    const head = rows[0].map(h => String(h).trim().toLowerCase());
    const iId = head.indexOf("user_id"), iName = head.indexOf("name");
    const iSal = head.indexOf("monthly_salary");
    let iKm = head.indexOf("rs_per_km");
    if (iKm < 0) iKm = head.indexOf("per_km_rate");
    if (iSal < 0 || (iId < 0 && iName < 0)) { toast.error("Columns needed: user_id (or name) and monthly_salary"); return; }
    const byId = new Map(people.map(p => [p.id, p]));
    const byName = new Map(people.map(p => [(p.name || "").trim().toLowerCase(), p]));
    const next = {};
    let hit = 0, miss = 0;
    rows.slice(1).forEach(r => {
      const p = (iId >= 0 && byId.get(String(r[iId] || "").trim())) || (iName >= 0 && byName.get(String(r[iName] || "").trim().toLowerCase()));
      if (!p) { miss++; return; }
      next[p.id] = { monthly_salary: String(r[iSal] ?? "").trim(), ...(iKm >= 0 ? { per_km_rate: String(r[iKm] ?? "").trim() } : {}) };
      hit++;
    });
    setEdits(prev => ({ ...prev, ...next }));
    toast.success(`Imported ${hit} row(s)${miss ? `, ${miss} not matched` : ""}. Check, then press Save all changes.`);
  };
  const s = data?.summary;
  const ctx = { edits, setEdit, history: (n) => setHist(n) };
  return (
    <MasterCtx.Provider value={ctx}>
      <div data-testid="hr-salary-master">
        <PageHeader title="Salary Master" subtitle="Enter each person's monthly salary first. Payroll counts only staff whose salary is set. A salary applies from the month you choose; older months keep the older salary." />
        <Card className="p-3 mb-3">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm text-slate-600 flex items-center gap-2">Effective from
              <Input type="month" value={month} onChange={e => e.target.value && (setMonth(e.target.value), setEdits({}))} className="w-44" data-testid="hr-sal-month" />
            </label>
            <div className="relative max-w-xs flex-1 min-w-[180px]">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name..." className="pl-9" />
            </div>
            {!q.trim() && <>
              <Button variant="outline" onClick={() => setOpen(new Set(allIds(data)))}>Open all</Button>
              <Button variant="outline" onClick={() => setOpen(new Set())}>Close all</Button>
            </>}
            <Button variant="outline" onClick={template} disabled={!data}>Download template</Button>
            <Button variant="outline" onClick={() => document.getElementById("hr-sal-file").click()} disabled={!data}>Import CSV</Button>
            <input id="hr-sal-file" type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} />
            <Button onClick={saveAll} disabled={saving || !changed.length} data-testid="hr-sal-save">Save all changes{changed.length ? ` (${changed.length})` : ""}</Button>
          </div>
        </Card>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <Tile label="Staff" value={s ? s.members : "-"} />
          <Tile label="Salary set" value={s ? s.set : "-"} tone="text-emerald-600" />
          <Tile label="Not set" value={s ? s.not_set : "-"} tone="text-rose-600" />
          <Tile label="Total monthly salary" value={s ? inr(s.monthly_total) : "-"} />
        </div>
        {loading && <Card className="p-6 text-center text-slate-400">Loading...</Card>}
        {!loading && data && q.trim() && <Card className="p-2"><MasterRows rows={flatRows} /></Card>}
        {!loading && data && !q.trim() && <Teams data={data} open={open} toggle={toggle} Head={MasterHead} Rows={MasterRows} onRoute={() => {}} />}
        {hist && <SalaryHistory person={hist} onClose={() => setHist(null)} />}
      </div>
    </MasterCtx.Provider>
  );
}

export function HrPerformancePage() {
  const [month, setMonth] = useState(todayIst().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const [q, setQ] = useState("");
  const [open, setOpen, toggle] = useToggle();
  const [detailId, setDetailId] = useState(null);
  const [showRules, setShowRules] = useState(false);
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    dms.hrPayrollReport(month)
      .then(r => { if (alive) { setData(r); setDraft(r.rules); } })
      .catch(e => { if (alive) { setData(null); toast.error(errMsg(e)); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [month, tick]);
  const people = useMemo(() => allPeople(data), [data]);
  const flatRows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return term ? people.filter(n => (n.name || "").toLowerCase().includes(term)) : [];
  }, [people, q]);
  const save = async (uid, patch) => {
    const p = people.find(x => x.id === uid);
    if (!p) return;
    try {
      await dms.hrSaveSalary({
        user_id: uid,
        monthly_salary: patch.monthly_salary ?? p.monthly_salary,
        per_km_rate: patch.per_km_rate ?? p.per_km_rate,
      });
      toast.success("Saved");
      setTick(t => t + 1);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const saveRules = async () => {
    try {
      await dms.hrSaveSalaryRules(draft);
      toast.success("Rules saved");
      setTick(t => t + 1);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const doCsv = () => downloadCsv(`salary_${month}.csv`,
    ["Name", "Role", "Monthly salary", "Rs/km", "Full days", "Half days", "Absent", "Weekly off", "Late", "Payable days", "Earned", "Km allowance", "Net"],
    people.map(n => [n.name, ROLE_LABEL[n.role] || n.role, n.monthly_salary, n.per_km_rate, n.full_days, n.half_days, n.absent_days, n.weekly_offs, n.late_days, n.payable_days, n.earned, n.km_allowance, n.net]));
  const s = data?.summary;
  const r = data?.rules;
  const detail = detailId ? people.find(x => x.id === detailId) : null;
  const ctx = { save, details: (n) => setDetailId(n.id) };
  return (
    <PayCtx.Provider value={ctx}>
      <div data-testid="hr-performance-page">
        <PageHeader title="Attendance Performance & Salary" subtitle="Month-wise team performance and salary. Salary comes from Salary Master. Click a team to open it." />
        <Card className="p-3 mb-3">
          <div className="flex items-center gap-3 flex-wrap">
            <Input type="month" value={month} onChange={e => e.target.value && setMonth(e.target.value)} className="w-44" data-testid="hr-pay-month" />
            <div className="relative max-w-xs flex-1 min-w-[180px]">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name..." className="pl-9" />
            </div>
            {!q.trim() && <>
              <Button variant="outline" onClick={() => setOpen(new Set(allIds(data)))}>Open all</Button>
              <Button variant="outline" onClick={() => setOpen(new Set())}>Close all</Button>
            </>}
            <Button variant="outline" onClick={() => setShowRules(v => !v)} data-testid="hr-rules-toggle">Salary rules</Button>
            <Button variant="outline" onClick={doCsv} disabled={!data}>Download CSV</Button>
          </div>
        </Card>
        {data && <RunBar month={month} data={data} onChanged={() => setTick(t => t + 1)} />}
        {showRules && <RulesPanel draft={draft} setDraft={setDraft} onSave={saveRules} />}
        {data && (
          <div className="text-xs text-slate-500 mb-2">
            Full day = {r.full_hours}+ hours, half day = {r.half_hours}+ hours, less than that or no punch = absent | Weekly off: {r.weekly_off === "sunday" ? "Sunday (paid)" : "none"} | Per day salary = monthly / {data.divisor} days | Late = first punch-in after {r.late_after} IST{r.late_per_deduct ? ` (every ${r.late_per_deduct} late = half day cut)` : " (no deduction)"}.
            {data.partial ? " Month is in progress: counted till yesterday, today is pending, so amounts are 'earned so far'." : ""} Leave and holiday data is not in the system yet.
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-3">
          <Tile label="Staff" value={s ? s.members : "-"} />
          <Tile label="Monthly salary total" value={s ? inr(s.monthly_total) : "-"} />
          <Tile label="Payable (net)" value={s ? inr(s.net_total) : "-"} tone="text-emerald-600" />
          <Tile label="Absent days" value={s ? s.absent_days : "-"} tone="text-rose-600" />
          <Tile label="Late days" value={s ? s.late_days : "-"} />
          <Tile label="Salary not set" value={s ? s.no_salary : "-"} tone="text-amber-700" />
        </div>
        {loading && <Card className="p-6 text-center text-slate-400">Loading...</Card>}
        {!loading && data && q.trim() && <Card className="p-2"><PayRows rows={flatRows} /></Card>}
        {!loading && data && !q.trim() && <Teams data={data} open={open} toggle={toggle} Head={PayHead} Rows={PayRows} onRoute={() => {}} />}
        {detail && data && <PayDetails n={detail} data={data} onClose={() => setDetailId(null)} />}
      </div>
    </PayCtx.Provider>
  );
}

export default HrDashboardPage;
